import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { hasManagerAccess } from '../App';
import { DollarSign, Ruler, Clock, Hammer, CheckCircle, UserMinus, RotateCcw } from 'lucide-react';

const fmt = (n) => '$' + (n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = 'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-nova-500 focus:border-nova-500 outline-none text-sm';

const STATUS_BADGE = {
  awaiting_payment: { label: 'Installed · Awaiting Pay', cls: 'bg-amber-100 text-amber-700' },
  completed:        { label: 'Paid + Installed',         cls: 'bg-green-100 text-green-700' },
};

function Stat({ label, value, color, icon: Icon, subtitle }) {
  return (
    <div className="bg-white rounded-xl border p-5 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color || 'text-gray-800'}`}>{value}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}

export default function InstallPay() {
  const { user } = useAuth();
  const { userId } = useParams();
  const isManager = hasManagerAccess(user);
  const viewingSelf = !userId || Number(userId) === user?.id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payoutForm, setPayoutForm] = useState({
    amount: '',
    paid_date: new Date().toISOString().split('T')[0],
    notes: 'Install pay',
  });
  const [savingPayout, setSavingPayout] = useState(false);

  const load = () => {
    setLoading(true);
    const url = userId ? `/commissions/install-pay/${userId}` : '/commissions/install-pay';
    api.get(url).then(setData).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [userId]);

  const toggleSubcontracted = async (saleId, currentlySubbed) => {
    const msg = currentlySubbed
      ? 'Move this job back to Miguel\'s pay?'
      : 'Mark as subcontracted? Miguel will not be paid for this install.';
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/commissions/install-pay/sale/${saleId}/subcontracted`, {
        subcontracted: !currentlySubbed,
      });
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const handleLogPayout = async (e) => {
    e.preventDefault();
    if (!payoutForm.amount || !data?.user?.id) return;
    setSavingPayout(true);
    try {
      await api.post('/commissions/payout', {
        salesman_id: data.user.id,
        amount: Number(payoutForm.amount),
        paid_date: payoutForm.paid_date,
        notes: payoutForm.notes,
      });
      setPayoutForm({ amount: '', paid_date: new Date().toISOString().split('T')[0], notes: 'Install pay' });
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSavingPayout(false);
    }
  };

  if (loading) return <p className="text-gray-400 text-sm">Loading…</p>;
  if (!data) return <p className="text-gray-400 text-sm">No pay data available.</p>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Hammer className="w-7 h-7 text-nova-600" />
        <h1 className="text-2xl font-bold">
          {viewingSelf ? 'My Install Pay' : `${data.user.full_name}'s Install Pay`}
        </h1>
      </div>

      {/* Rate banner */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl px-4 py-3 mb-5 flex items-center gap-2">
        <Ruler className="w-4 h-4 text-orange-600" />
        <p className="text-sm text-orange-800">
          Pay rate: <span className="font-bold">{fmt(data.rate)}</span> per foot installed. Earned once job hits
          <span className="font-semibold"> "Installed · Awaiting Pay" </span>
          or <span className="font-semibold">"Paid + Installed"</span>.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat
          label="Total Jobs Installed"
          value={(data.jobCount || 0).toLocaleString()}
          color="text-gray-800"
          icon={CheckCircle}
        />
        <Stat
          label="Feet Installed"
          value={(data.totalFeet || 0).toLocaleString() + ' ft'}
          color="text-gray-800"
          icon={Ruler}
        />
        <Stat
          label="Total Earned"
          value={fmt(data.totalEarned)}
          color="text-green-600"
          icon={DollarSign}
        />
        <Stat
          label="Balance Owed"
          value={fmt(data.balanceOwed)}
          color={data.balanceOwed > 0 ? 'text-orange-500' : 'text-gray-400'}
          icon={Clock}
          subtitle={`${fmt(data.totalPaid)} already paid`}
        />
      </div>

      {/* Jobs table */}
      <div className="bg-white rounded-xl border overflow-hidden mb-6">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
          <h2 className="font-semibold text-gray-700">Installed Jobs</h2>
          {isManager && (
            <span className="text-xs text-gray-400">Use the action button to mark a job as subcontracted (excluded from pay).</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Install Date</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Customer</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">City</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Footage</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Pay</th>
                {isManager && <th className="px-4 py-3"></th>}
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.jobs.map(j => {
                const st = STATUS_BADGE[j.status] || { label: j.status, cls: 'bg-gray-100 text-gray-500' };
                const subbed = j.subcontracted;
                return (
                  <tr key={j.id} className={`hover:bg-gray-50 ${subbed ? 'bg-gray-50/50 text-gray-400' : ''}`}>
                    <td className="px-4 py-3">{j.install_date || '—'}</td>
                    <td className={`px-4 py-3 font-medium ${subbed ? 'line-through' : ''}`}>{j.customer_name}</td>
                    <td className="px-4 py-3">{j.customer_city || '—'}</td>
                    <td className={`px-4 py-3 text-right ${subbed ? 'line-through' : ''}`}>{(j.total_footage || 0).toLocaleString()} ft</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5 items-start">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                        {subbed && <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">Subcontracted</span>}
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-right font-semibold ${subbed ? 'text-gray-400' : 'text-green-600'}`}>
                      {subbed ? '—' : fmt(j.pay)}
                    </td>
                    {isManager && (
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => toggleSubcontracted(j.id, !!subbed)}
                          className={`text-xs flex items-center gap-1 px-2 py-1 rounded-lg font-medium whitespace-nowrap ${
                            subbed
                              ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                              : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200'
                          }`}>
                          {subbed ? <><RotateCcw className="w-3 h-3" /> Restore</> : <><UserMinus className="w-3 h-3" /> Sub</>}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {data.jobs.length === 0 && (
                <tr><td colSpan={isManager ? 7 : 6} className="px-4 py-6 text-center text-gray-400">No installs yet</td></tr>
              )}
              {data.jobs.length > 0 && (
                <tr className="bg-gray-50 font-semibold">
                  <td colSpan={3} className="px-4 py-3 text-right text-gray-600">Total (Miguel)</td>
                  <td className="px-4 py-3 text-right">{data.totalFeet.toLocaleString()} ft</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right text-green-600">{fmt(data.totalEarned)}</td>
                  {isManager && <td className="px-4 py-3"></td>}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payouts */}
      <div className="bg-white rounded-xl border overflow-hidden mb-6">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h2 className="font-semibold text-gray-700">Payout History</h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Date</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Amount</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Notes</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Paid By</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.payouts.map(p => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">{p.paid_date}</td>
                <td className="px-4 py-3 font-semibold text-blue-600">{fmt(p.amount)}</td>
                <td className="px-4 py-3 text-gray-400">{p.notes || '-'}</td>
                <td className="px-4 py-3 text-gray-500">{p.paid_by_name || '-'}</td>
              </tr>
            ))}
            {data.payouts.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No payouts recorded</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {isManager && (
        <div className="bg-white rounded-xl border p-5">
          <h2 className="font-semibold text-gray-700 mb-4">Record Payout</h2>
          <form onSubmit={handleLogPayout}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Amount</label>
                <input type="number" step="0.01" required value={payoutForm.amount}
                  onChange={e => setPayoutForm(f => ({ ...f, amount: e.target.value }))}
                  className={inputCls} placeholder="0.00" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Date</label>
                <input type="date" required value={payoutForm.paid_date}
                  onChange={e => setPayoutForm(f => ({ ...f, paid_date: e.target.value }))}
                  className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <input value={payoutForm.notes}
                  onChange={e => setPayoutForm(f => ({ ...f, notes: e.target.value }))}
                  className={inputCls} />
              </div>
            </div>
            <button type="submit" disabled={savingPayout || !payoutForm.amount}
              className="px-5 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50">
              {savingPayout ? 'Saving…' : 'Record Payout'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
